import React, { createContext, useState, useContext, useEffect } from 'react';

// Define available languages
export const languages = {
  en: 'English',
  ge: 'Georgian',
  ru: 'Russian'
};

// Create translations object
export const translations = {
  en: {
    // Navigation
    home: 'Home',
    services: 'Services',
    about: 'About Us',
    features: 'Features',
    testimonials: 'Testimonials',
    testimonialsTitle: 'Testimonials',
    testimonialsSubtitle: 'Verified reviews and feedback from enterprise partners, tech leaders, and businesses we safeguard.',
    contact: 'Contact',
    
    // Hero Section
    heroTitle: 'Welcome to Our IT Support Service',
    heroSubtitle: 'We provide comprehensive IT solutions tailored to your business needs. Our team of experts is dedicated to ensuring your IT systems run seamlessly, allowing you to focus on your core business objectives.',
    contactUs: 'Contact Us',
    learnMore: 'Learn More',
    
    // Services Section
    servicesTitle: 'Services',
    service1: 'Operating System and Software Installation',
    service2: 'Scripting for Automation and Task Simplification',
    service3: 'Custom Software Development',
    service4: 'Desktop Task Simplification',
    service5: 'Network Security',
    service6: 'Web Development',
    service7: 'Web3 Solutions',
    service8: 'Local LLM Setup',
    service9: 'Advanced Cybersecurity',
    service1Desc: 'Professional installation of Windows, Linux, and macOS along with essential software suites, drivers, and optimization for peak performance.',
    service2Desc: 'Custom Python, Bash, or PowerShell scripts to automate repetitive tasks, manage files, and streamline your daily digital workflow.',
    service3Desc: 'End-to-end development of bespoke software applications tailored to your specific business requirements and scalability needs.',
    service4Desc: 'UI/UX optimizations and tool configurations to make your desktop environment more intuitive and productive for your specific use case.',
    service5Desc: 'Comprehensive network hardening, firewall configuration, and secure VPN setup to protect your data from external threats.',
    service6Desc: 'Modern, responsive, and high-performance websites built with the latest technologies like React and premium design systems.',
    service7Desc: 'Blockchain integration, smart contract development, and decentralized application (dApp) solutions for the next generation of the web.',
    service8Desc: 'Installation and optimization of private, offline AI models (like Llama 3) on your hardware for secure and private AI assistance.',
    service9Desc: 'Proactive vulnerability scanning, penetration testing, and risk management strategies to secure your digital infrastructure.',
    
    // About Section
    aboutTitle: 'About Us',
    aboutContent: 'At IT For Sec, we are a leading IT support service provider dedicated to delivering exceptional solutions to businesses of all sizes. With our team of highly skilled professionals and extensive industry experience, we are committed to helping our clients optimize their IT infrastructure and maximize productivity.',
    
    // Features Section
    featuresTitle: 'More details:',
    feature1Title: 'IT Infrastructure Management',
    feature1Content: 'We offer comprehensive IT infrastructure management services, ensuring that your systems are running efficiently and securely. From network setup and maintenance to server management and data backup, we handle it all.',
    feature2Title: 'Software Solutions',
    feature2Content: 'Whether you need customized software development, software installation, or integration with existing systems, we have the expertise to deliver high-quality solutions tailored to your unique business requirements.',
    feature3Title: 'Network Security',
    feature3Content: 'We understand the importance of protecting your valuable data and ensuring network security. Our team implements robust security measures to safeguard your systems against potential threats, ensuring the integrity and confidentiality of your information.',
    feature4Title: 'Automation and Efficiency',
    feature4Content: 'We specialize in automating repetitive tasks, streamlining workflows, and improving overall efficiency. By leveraging cutting-edge technologies and scripting solutions, we help businesses save time and reduce manual errors.',
    feature5Title: 'Web Development',
    feature5Content: 'Our skilled web developers create visually appealing and user-friendly websites that effectively represent your brand and engage your target audience. We utilize modern web technologies to deliver responsive and optimized websites.',
    feature6Title: 'Consulting and Support',
    feature6Content: 'As your trusted IT partner, we provide expert consulting services to help you make informed decisions about your IT strategy. We also offer ongoing support to ensure that your systems operate smoothly and are always up to date.',
    
    // Contact Section
    contactTitle: 'Contact Us',
    email: 'Email: info@itforsec.com',
    phone: 'Phone: +995 599-400-135',
    nameLabel: 'Your Name',
    emailLabel: 'Your Email',
    messageLabel: 'Your Message',
    submitButton: 'Send Message',
    
    // FAQ Section
    faq: 'FAQ',
    faqTitle: 'Frequently Asked Questions',
    faqSubtitle: 'Find answers to common questions about our services and how we can help your business.',
    faq1Q: 'What services do you offer?',
    faq1A: 'We offer a wide range of IT services including software installation, custom software development, network security, web development, automation solutions, and Local LLM setup.',
    faq2Q: 'How quickly do you respond to support requests?',
    faq2A: 'We typically respond to support requests within 2-4 hours during business hours. For urgent issues, our response time is even faster.',
    faq3Q: 'Do you offer remote support?',
    faq3A: 'Yes, we provide remote support services for most software and configuration issues, allowing for quick resolution without the need for an on-site visit.',
    faq4Q: 'What are your working hours?',
    faq4A: 'Our standard business hours are Monday to Friday, 9:00 AM to 6:00 PM. However, we also offer emergency support outside these hours for critical issues.',
    faq5Q: 'Do you provide hardware as well as software support?',
    faq5A: 'Yes, we provide comprehensive support for both hardware and software, including hardware troubleshooting, upgrades, and maintenance.',
    
    // Footer
    copyright: `© ${new Date().getFullYear()} ITFORSEC. All rights reserved.`,
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    footerDesc: 'Providing comprehensive IT solutions tailored to your business needs. Our team of experts is dedicated to ensuring your IT systems run seamlessly.',
    quickLinks: 'Quick Links',
    contactInfo: 'Contact Info',
    whatsappPrompt: 'Need urgent assistance? Contact our support team via WhatsApp for immediate help.',
    close: 'Close'
  },
  ge: {
    // Navigation
    home: 'მთავარი',
    services: 'სერვისები',
    about: 'ჩვენს შესახებ',
    features: 'ფუნქციები',
    testimonials: 'შეფასებები',
    testimonialsTitle: 'კლიენტების შეფასებები',
    testimonialsSubtitle: 'ვერიფიცირებული შეფასებები და გამოხმაურებები ჩვენი პარტნიორებისა და კომპანიებისგან.',
    contact: 'კონტაქტი',
    
    // Hero Section
    heroTitle: 'IT მხარდაჭერის სერვისი',
    heroSubtitle: 'ჩვენ გთავაზობთ სრულყოფილ IT სოლუშიონს, რომლებიც მორგებულია თქვენი ბიზნესის საჭიროებებზე.',
    contactUs: 'დაგვიკავშირდით',
    learnMore: 'მეტის გაგება',
    
    // Services Section
    servicesTitle: 'სერვისები',
    service1: 'ოპერაციული სისტემებისა და პროგრამული პროდუქტების ინსტალაცია',
    service2: 'სპეციფიკური სკრიპტების წერა სისტემის აუტომატიზაციისა და გამარტივებისთვის',
    service3: 'პროგრამული პროდუქტის შექმნა და მორგება',
    service4: 'დესკტოპთან მუშაობის გამარტივება',
    service5: 'ქსელის უსაფრთხოება',
    service6: 'ვებ-საიტების დეველოპმენტი',
    service7: 'Web3-ზე დაფუძნებული პროექტები',
    service8: 'ლოკალური LLM-ის კონფიგურაცია',
    service9: 'გაფართოებული კიბერუსაფრთხოება',
    service1Desc: 'Windows-ის, Linux-ის და macOS-ის პროფესიონალური ინსტალაცია, აუცილებელი პროგრამული უზრუნველყოფის პაკეტებთან, დრაივერებთან და მაქსიმალური წარმადობის ოპტიმიზაციასთან ერთად.',
    service2Desc: 'ინდივიდუალური Python, Bash ან PowerShell სკრიპტები განმეორებადი ამოცანების ავტომატიზაციისთვის, ფაილების მართვისთვის და თქვენი ყოველდღიური ციფრული სამუშაო პროცესის გასამარტივებლად.',
    service3Desc: 'ინდივიდუალური პროგრამული აპლიკაციების სრული ციკლის შემუშავება, რომლებიც მორგებულია თქვენი ბიზნესის სპეციფიკურ მოთხოვნებსა და მასშტაბურობის საჭიროებებზე.',
    service4Desc: 'UI/UX ოპტიმიზაცია და ხელსაწყოების კონფიგურაცია, რათა თქვენი სამუშაო მაგიდა გახდეს უფრო ინტუიციური და პროდუქტიული თქვენი კონკრეტული საჭიროებებისთვის.',
    service5Desc: 'ქსელის ყოვლისმომცველი გაძლიერება, ფაირვოლის კონფიგურაცია და უსაფრთხო VPN-ის დაყენება თქვენი მონაცემების გარე საფრთხეებისგან დასაცავად.',
    service6Desc: 'თანამედროვე, რესპონსიული და მაღალი წარმადობის ვებსაიტები, შექმნილი უახლესი ტექნოლოგიებით, როგორიცაა React და პრემიუმ დიზაინის სისტემები.',
    service7Desc: 'ბლოკჩეინის ინტეგრაცია, სმარტ-კონტრაქტების შემუშავება და დეცენტრალიზებული აპლიკაციების (dApp) გადაწყვეტილებები ვების შემდეგი თაობისთვის.',
    service8Desc: 'პირადი, ოფლაინ AI მოდელების (მაგალითად, Llama 3) ინსტალაცია და ოპტიმიზაცია თქვენს აპარატურაზე უსაფრთხო და პირადი AI ასისტენტისთვის.',
    service9Desc: 'პროაქტიული მოწყვლადობის სკანირება, შეღწევადობის ტესტირება და რისკების მართვის სტრატეგიები თქვენი ციფრული ინფრასტრუქტურის დასაცავად.',
    
    // About Section
    aboutTitle: 'ჩვენს შესახებ',
    aboutContent: 'ITFORSEC-ში ჩვენ ვართ წამყვანი IT მხარდაჭერის მიმწოდებლები, რომელიც უზრუნველყოფს განსაკუთრებული გადაწყვეტილებების მიწოდებას ყველა ზომის ბიზნესისთვის. ITFORSEC-ი გახლავთ: მაღალკვალიფიციური პროფესიონალების გუნდით და ინდუსტრიის ფართო გამოცდილებით. ჩვენ მზად ვართ დავეხმაროთ ჩვენს კლიენტებს IT ინფრასტრუქტურის ოპტიმიზაციაში და მაქსიმალური პროდუქტიულობის გაზრდაში.',
    
    // Features Section
    featuresTitle: 'მეტი ინფორმაცია:',
    feature1Title: 'IT ინფრასტრუქტურის მენეჯმენტი',
    feature1Content: 'ჩვენ გთავაზობთ IT ინფრასტრუქტურის მართვის სრულ სერვისებს, რაც უზრუნველყოფს თქვენი სისტემების ეფექტურად და უსაფრთხოდ მუშაობას. ქსელის დაყენებიდან და შენარჩუნებიდან დაწყებული სერვერის მენეჯმენტამდე და მონაცემთა სარეზერვო შენახვამდე, ჩვენ ყველაფერს ვუზრუნველყოფთ.',
    feature2Title: 'პროგრამული გადაწყვეტილებები',
    feature2Content: 'გჭირდებათ თუ არა მორგებული პროგრამული უზრუნველყოფის შემუშავება? პროგრამული უზრუნველყოფის ინსტალაცია ან ინტეგრაცია არსებულ სისტემებთან? ჩვენ გვაქვს გამოცდილება, რომ მივიღოთ მაღალი ხარისხის გადაწყვეტილებები, რომლებიც მორგებული იქნება თქვენს უნიკალურ ბიზნეს მოთხოვნებზე.',
    feature3Title: 'ქსელის უსაფრთხოება',
    feature3Content: 'ჩვენ გვესმის თქვენი ღირებული მონაცემების დაცვისა და ქსელის უსაფრთხოების უზრუნველყოფის მნიშვნელობა. ჩვენი გუნდი ახორციელებს უსაფრთხოების მკაცრ ზომებს თქვენი სისტემების პოტენციური საფრთხეებისგან დასაცავად, თქვენი ინფორმაციის მთლიანობისა და კონფიდენციალურობის უზრუნველსაყოფად.',
    feature4Title: 'ავტომატიზაცია და ეფექტურობა',
    feature4Content: 'ჩვენ სპეციალიზირებული ვართ განმეორებადი ამოცანების ავტომატიზაციაში, სამუშაო ნაკადების გამარტივებაში და საერთო ეფექტურობის გაუმჯობესებაში. უახლესი ტექნოლოგიებისა და სკრიპტირების გადაწყვეტილებების გამოყენებით, ჩვენ ვეხმარებით ბიზნესს დაზოგონ დრო და შეამცირონ საქმეში შეცდომები.',
    feature5Title: 'ვებ-დეველოპმენტი',
    feature5Content: 'ჩვენი გამოცდილი ვებ დეველოპერები ქმნიან ვიზუალურად მიმზიდველ და მოსახერხებელი ვებსაიტებს, რომლებიც ეფექტურად წარმოადგენენ თქვენს ბრენდს და კონცენტრირდებიან თქვენს მიერ გამიზნულ აუდიტორიაზე. ჩვენ ვიყენებთ თანამედროვე ვებ ტექნოლოგიებს ოპტიმიზებული ვებსაიტების მისაწოდებლად.',
    feature6Title: 'კონსულტაცია და მხარდაჭერა',
    feature6Content: 'როგორც თქვენი სანდო IT პარტნიორი, ჩვენ გთავაზობთ ექსპერტთა საკონსულტაციო მომსახურებას, რათა დაგეხმაროთ მიიღოთ ინფორმირებული გადაწყვეტილებები თქვენი IT სტრატეგიის შესახებ. ჩვენ ასევე გთავაზობთ მუდმივ მხარდაჭერას იმის უზრუნველსაყოფად, რომ თქვენი სისტემები მუშაობენ შეუფერხებლად და ყოველთვის განახლებულ რეჟიმში.',
    
    // Contact Section
    contactTitle: 'დაგვიკავშირდით',
    email: 'ელ.ფოსტა: info@itforsec.com',
    phone: 'ტელეფონი: +995 599-400-135',
    nameLabel: 'თქვენი სახელი',
    emailLabel: 'თქვენი ელ.ფოსტა',
    messageLabel: 'თქვენი შეტყობინება',
    submitButton: 'გაგზავნა',
    
    // FAQ Section
    faq: 'FAQ',
    faqTitle: 'ხშირად დასმული კითხვები',
    faqSubtitle: 'იპოვეთ პასუხები გავრცელებულ კითხვებზე ჩვენი სერვისებისა და თქვენი ბიზნესის დახმარების შესახებ.',
    faq1Q: 'რა სერვისებს გვთავაზობთ?',
    faq1A: 'ჩვენ გთავაზობთ IT სერვისების ფართო სპექტრს, მათ შორის პროგრამული უზრუნველყოფის ინსტალაციას, ინდივიდუალურ პროგრამულ უზრუნველყოფას, ქსელის უსაფრთხოებას, ვებ-დეველოპმენტს, ავტომატიზაციის გადაწყვეტილებებს და ლოკალური LLM-ის კონფიგურაციას.',
    faq2Q: 'რამდენად სწრაფად რეაგირებთ მხარდაჭერის მოთხოვნებზე?',
    faq2A: 'ჩვენ ჩვეულებრივ ვპასუხობთ მხარდაჭერის მოთხოვნებს 2-4 საათის განმავლობაში სამუშაო საათებში. გადაუდებელი საკითხებისთვის რეაგირების დრო კიდევ უფრო სწრაფია.',
    faq3Q: 'გთავაზობთ თუ არა დისტანციურ მხარდაჭერას?',
    faq3A: 'დიახ, ჩვენ გთავაზობთ დისტანციური მხარდაჭერის სერვისებს პროგრამული უზრუნველყოფისა და კონფიგურაციის საკითხების უმეტესობისთვის, რაც საშუალებას იძლევა სწრაფი გადაწყვეტისთვის ადგილზე ვიზიტის საჭიროების გარეშე.',
    faq4Q: 'როგორია თქვენი სამუშაო საათები?',
    faq4A: 'ჩვენი სტანდარტული სამუშაო საათებია ორშაბათიდან პარასკევის ჩათვლით, დილის 9:00-დან საღამოს 6:00-მდე. თუმცა, ჩვენ ასევე გთავაზობთ გადაუდებელ მხარდაჭერას კრიტიკული საკითხებისთვის ამ საათების გარეთ.',
    faq5Q: 'უზრუნველყოფთ თუ არა როგორც აპარატურულ, ისე პროგრამულ მხარდაჭერას?',
    faq5A: 'დიახ, ჩვენ გთავაზობთ ყოვლისმომცველ მხარდაჭერას როგორც აპარატურის, ასევე პროგრამული უზრუნველყოფისთვის, მათ შორის აპარატურის დიაგნოსტიკას, განახლებას და ტექნიკურ მომსახურებას.',
    
    // Footer
    copyright: `© ${new Date().getFullYear()} ITFORSEC. ყველა უფლება დაცულია.`,
    privacyPolicy: 'კონფიდენციალურობის პოლიტიკა',
    termsOfService: 'მომსახურების პირობები',
    footerDesc: 'ჩვენ გთავაზობთ სრულყოფილ IT გადაწყვეტილებებს, რომლებიც მორგებულია თქვენი ბიზნესის საჭიროებებზე. ჩვენი ექსპერტთა გუნდი უზრუნველყოფს თქვენი სისტემების შეუფერხებელ მუშაობას.',
    quickLinks: 'სწრაფი ბმულები',
    contactInfo: 'საკონტაქტო ინფორმაცია',
    whatsappPrompt: 'გჭირდებათ სასწრაფო დახმარება? დაუკავშირდით ჩვენს მხარდაჭერის გუნდს WhatsApp-ის საშუალებით.',
    close: 'დახურვა'
  },
  ru: {
    // Navigation
    home: 'Главная',
    services: 'Услуги',
    about: 'О нас',
    features: 'Возможности',
    testimonials: 'Отзывы',
    testimonialsTitle: 'Отзывы клиентов',
    testimonialsSubtitle: 'Подтвержденные отзывы и обратная связь от наших корпоративных партнеров и клиентов.',
    contact: 'Контакты',

    // Hero Section
    heroTitle: 'IT-поддержка и сервис',
    heroSubtitle: 'Мы предоставляем комплексные IT-решения, адаптированные под потребности вашего бизнеса. Наша команда экспертов обеспечивает бесперебойную работу IT-систем.',
    contactUs: 'Связаться с нами',
    learnMore: 'Узнать больше',

    // Services Section
    servicesTitle: 'Услуги',
    service1: 'Установка ОС и ПО',
    service2: 'Скрипты для автоматизации',
    service3: 'Разработка ПО',
    service4: 'Оптимизация рабочего стола',
    service5: 'Сетевая безопасность',
    service6: 'Веб-разработка',
    service7: 'Web3 решения',
    service8: 'Настройка локальных LLM',
    service9: 'Кибербезопасность',
    service1Desc: 'Профессиональная установка Windows, Linux и macOS с необходимым ПО, драйверами и оптимизацией производительности.',
    service2Desc: 'Индивидуальные скрипты на Python, Bash или PowerShell для автоматизации задач, управления файлами и оптимизации цифровых процессов.',
    service3Desc: 'Комплексная разработка индивидуального ПО, адаптированного под ваши бизнес-требования и потребности масштабирования.',
    service4Desc: 'Оптимизация UI/UX и настройка инструментов для повышения интуитивности и продуктивности вашей рабочей среды.',
    service5Desc: 'Комплексное укрепление сети, настройка фаерволов и безопасных VPN для защиты данных от внешних угроз.',
    service6Desc: 'Современные, адаптивные и высокопроизводительные веб-сайты, созданные с использованием передовых технологий, таких как React.',
    service7Desc: 'Интеграция блокчейна, разработка смарт-контрактов и децентрализованных приложений (dApps) для следующего поколения веба.',
    service8Desc: 'Установка и оптимизация приватных офлайн AI-моделей (например, Llama 3) на вашем оборудовании для безопасного ИИ-ассистента.',
    service9Desc: 'Проактивное сканирование уязвимостей, тестирование на проникновение и стратегии управления рисками для защиты цифровой инфраструктуры.',

    // About Section
    aboutTitle: 'О нас',
    aboutContent: 'В ITFORSEC мы являемся ведущим поставщиком IT-поддержки, предоставляющим исключительные решения для бизнеса любого размера. Наша команда высококвалифицированных профессионалов имеет обширный опыт в индустрии и помогает клиентам оптимизировать IT-инфраструктуру.',

    // Features Section
    featuresTitle: 'Подробнее:',
    feature1Title: 'Управление IT-инфраструктурой',
    feature1Content: 'Мы предлагаем комплексные услуги по управлению IT-инфраструктурой, обеспечивая эффективную и безопасную работу ваших систем. От настройки сети до управления серверами и резервного копирования данных — мы берем всё на себя.',
    feature2Title: 'Программные решения',
    feature2Content: 'Нужна ли вам разработка индивидуального ПО, установка программ или интеграция с существующими системами — мы обладаем опытом для создания качественных решений под ваши уникальные требования.',
    feature3Title: 'Сетевая безопасность',
    feature3Content: 'Мы понимаем важность защиты ваших данных. Наша команда внедряет надежные меры безопасности для защиты систем от потенциальных угроз.',
    feature4Title: 'Автоматизация и эффективность',
    feature4Content: 'Мы специализируемся на автоматизации повторяющихся задач, оптимизации рабочих процессов и повышении общей эффективности бизнеса.',
    feature5Title: 'Веб-разработка',
    feature5Content: 'Наши опытные веб-разработчики создают визуально привлекательные и удобные сайты, эффективно представляющие ваш бренд и привлекающие целевую аудиторию.',
    feature6Title: 'Консультации и поддержка',
    feature6Content: 'Как ваш надежный IT-партнер, мы предоставляем экспертные консультации и постоянную поддержку для обеспечения бесперебойной работы ваших систем.',

    // Contact Section
    contactTitle: 'Contact Us!',
    email: 'Email: info@itforsec.com',
    phone: 'Телефон: +995 599-400-135',
    nameLabel: 'Ваше имя',
    emailLabel: 'Ваш Email',
    messageLabel: 'Ваше сообщение',
    submitButton: 'Отправить',
    getInTouch: 'Связаться с нами',

    // FAQ Section
    faq: 'FAQ',
    faqTitle: 'Часто задаваемые вопросы',
    faqSubtitle: 'Найдите ответы на вопросы о наших услугах и том, как мы можем помочь вашему бизнесу.',
    faq1Q: 'Какие услуги вы предоставляете?',
    faq1A: 'Мы предлагаем широкий спектр IT-услуг: установку ПО, разработку ПО, сетевую безопасность, веб-разработку, решения для автоматизации и настройку локальных LLM.',
    faq2Q: 'Как быстро вы отвечаете на запросы?',
    faq2A: 'Обычно мы отвечаем на запросы в течение 2-4 часов в рабочее время. Для срочных вопросов время отклика еще быстрее.',
    faq3Q: 'Предоставляете ли вы удаленную поддержку?',
    faq3A: 'Да, мы предоставляем услуги удаленной поддержки для большинства программных и конфигурационных вопросов, что позволяет быстро решать проблемы без визита на место.',
    faq4Q: 'Каков ваш график работы?',
    faq4A: 'Наши стандартные часы работы: понедельник-пятница с 9:00 до 18:00. Также мы предоставляем экстренную поддержку в другое время для критических вопросов.',
    faq5Q: 'Обеспечиваете ли вы поддержку оборудования и ПО?',
    faq5A: 'Да, мы предоставляем комплексную поддержку как для оборудования, так и для ПО, включая диагностику, обновления и техническое обслуживание.',

    // Footer
    copyright: `© ${new Date().getFullYear()} ITFORSEC. Все права защищены.`,
    privacyPolicy: 'Политика конфиденциальности',
    termsOfService: 'Условия использования',
    footerDesc: 'Комплексные IT-решения, адаптированные под ваши потребности. Наша команда экспертов обеспечивает бесперебойную работу ваших IT-систем.',
    quickLinks: 'Быстрые ссылки',
    contactInfo: 'Контактная информация',
    whatsappPrompt: 'Нужна срочная помощь? Свяжитесь с нашей службой поддержки через WhatsApp.',
    close: 'Закрыть'
  }
};

// Create context
const LanguageContext = createContext({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
  dynamicContent: null,
  contactInfo: null,
  companyInfo: null,
  servicesList: null,
  testimonialsList: null,
  faqsList: null,
});

// Create provider component
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [dynamicContent, setDynamicContent] = useState(null);
  const [contentTranslations, setContentTranslations] = useState(translations);

  // Sync content dynamically from PHP flat-file API or static fallback
  useEffect(() => {
    let isMounted = true;
    const fetchContent = async () => {
      try {
        let content = null;
        try {
          const res = await fetch('/api/content.php?t=' + Date.now());
          if (res.ok) {
            const json = await res.json();
            content = json.data || json;
          }
        } catch (e) {
          // If PHP backend is not running or returns raw script, fall back to static JSON
        }

        if (!content) {
          try {
            const fallbackRes = await fetch('/data/content.json?t=' + Date.now());
            if (fallbackRes.ok) {
              const json = await fallbackRes.json();
              content = json.data || json;
            }
          } catch (e) {}
        }

        if (content && isMounted) {
            setDynamicContent(content);
            if (content.sections) {
              setContentTranslations(prev => {
                const next = { ...prev };
                // Populate all languages present in content.sections
                Object.keys(content.sections).forEach(lCode => {
                  next[lCode] = {
                    ...(prev[lCode] || prev.en || {}),
                    ...(content.sections[lCode] || {})
                  };
                });
                // Also update service titles & descriptions for all languages
                if (Array.isArray(content.services)) {
                  content.services.forEach((srv, i) => {
                    const idx = i + 1;
                    Object.keys(next).forEach(lCode => {
                      if (srv.title && srv.title[lCode]) next[lCode][`service${idx}`] = srv.title[lCode];
                      if (srv.desc && srv.desc[lCode]) next[lCode][`service${idx}Desc`] = srv.desc[lCode];
                    });
                  });
                }
                return next;
              });
            }
          }
      } catch (err) {
        // Fallback silently to default built-in translations
      }
    };

    fetchContent();
    return () => { isMounted = false; };
  }, []);

  // Synchronize document title and meta tags when language or dynamic content changes
  useEffect(() => {
    if (!dynamicContent) return;
    const seo = dynamicContent.seo || {};
    const social = dynamicContent.social || {};

    const activeTitle = seo.titles?.[language] || seo.titles?.en || document.title;
    const activeDesc = seo.descriptions?.[language] || seo.descriptions?.en || '';
    const activeKeywords = seo.keywords?.[language] || seo.keywords?.en || '';

    const ogTitle = social.og_titles?.[language] || social.og_titles?.en || activeTitle;
    const ogDesc = social.og_descriptions?.[language] || social.og_descriptions?.en || activeDesc;
    const ogImage = social.og_image || '';

    // Update document title
    if (activeTitle) {
      document.title = activeTitle;
    }

    // Helper to set or create meta tag
    const setMetaTag = (selector, attribute, value) => {
      if (!value) return;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        if (selector.startsWith('meta[name=')) {
          const name = selector.match(/name=["']([^"']+)["']/)?.[1];
          if (name) el.setAttribute('name', name);
        } else if (selector.startsWith('meta[property=')) {
          const prop = selector.match(/property=["']([^"']+)["']/)?.[1];
          if (prop) el.setAttribute('property', prop);
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attribute, value);
    };

    if (activeDesc) {
      setMetaTag('meta[name="description"]', 'content', activeDesc);
    }
    if (activeKeywords) {
      setMetaTag('meta[name="keywords"]', 'content', activeKeywords);
    }
    if (ogTitle) {
      setMetaTag('meta[property="og:title"]', 'content', ogTitle);
      setMetaTag('meta[property="twitter:title"]', 'content', ogTitle);
    }
    if (ogDesc) {
      setMetaTag('meta[property="og:description"]', 'content', ogDesc);
      setMetaTag('meta[property="twitter:description"]', 'content', ogDesc);
    }
    if (ogImage) {
      setMetaTag('meta[property="og:image"]', 'content', ogImage);
      setMetaTag('meta[property="twitter:image"]', 'content', ogImage);
    }
    if (social.og_type) {
      setMetaTag('meta[property="og:type"]', 'content', social.og_type);
    }
    if (social.og_site_name) {
      setMetaTag('meta[property="og:site_name"]', 'content', social.og_site_name);
    }
    if (seo.canonical_url) {
      let canonicalEl = document.querySelector('link[rel="canonical"]');
      if (canonicalEl) {
        canonicalEl.setAttribute('href', seo.canonical_url);
      }
    }
  }, [language, dynamicContent]);

  // Translation function with English fallback for missing keys
  const t = (key) => {
    if (contentTranslations[language] && contentTranslations[language][key] !== undefined && contentTranslations[language][key] !== '') {
      return contentTranslations[language][key];
    }
    if (contentTranslations.en && contentTranslations.en[key] !== undefined) {
      return contentTranslations.en[key];
    }
    return key;
  };
  
  const defaultSectionVisibility = {
    hero: true,
    services: true,
    about: true,
    features: true,
    testimonials: true,
    faq: true,
    contact: true,
    newsletter: false,
    pwa: true
  };

  const sectionVisibility = dynamicContent?.section_visibility
    ? { ...defaultSectionVisibility, ...dynamicContent.section_visibility }
    : defaultSectionVisibility;

  const isSectionVisible = (sectionKey) => {
    return sectionVisibility[sectionKey] !== false;
  };
  
  const rawLanguages = dynamicContent?.languages || {
    en: { name: 'English', flag: '🇬🇧', native: 'English', enabled: true },
    ge: { name: 'Georgian', flag: '🇬🇪', native: 'ქართული', enabled: true },
    ru: { name: 'Russian', flag: '🇷🇺', native: 'Русский', enabled: true }
  };

  // Only include active/enabled languages on the website (enabled !== false)
  const availableLanguages = Object.keys(rawLanguages).reduce((acc, code) => {
    if (rawLanguages[code].enabled !== false) {
      acc[code] = rawLanguages[code];
    }
    return acc;
  }, {});

  // Fallback if all happen to be disabled
  if (Object.keys(availableLanguages).length === 0) {
    availableLanguages.en = rawLanguages.en || { name: 'English', flag: '🇬🇧', native: 'English', enabled: true };
  }

  // If currently active language is turned off, switch to first available enabled language
  useEffect(() => {
    if (availableLanguages && !availableLanguages[language]) {
      const fallbackLang = Object.keys(availableLanguages)[0] || 'en';
      setLanguage(fallbackLang);
    }
  }, [availableLanguages, language]);

  const value = {
    language,
    setLanguage,
    availableLanguages,
    allLanguages: rawLanguages,
    t,
    dynamicContent,
    contactInfo: dynamicContent?.contact_info || null,
    companyInfo: dynamicContent?.company_info || null,
    servicesList: dynamicContent?.services || null,
    testimonialsList: dynamicContent?.testimonials || null,
    testimonialsUi: dynamicContent?.testimonials_ui || null,
    faqsList: dynamicContent?.faqs || null,
    siteSettings: dynamicContent?.settings || null,
    newsletterUi: dynamicContent?.newsletter_ui || null,
    commandPaletteConfig: dynamicContent?.command_palette || null,
    adminSlug: dynamicContent?.settings?.admin_slug || 'admin',
    showFooterAdminLink: dynamicContent?.settings?.show_footer_admin_link !== false,
    sectionVisibility,
    isSectionVisible,
  };
  
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for using the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const useContent = () => useLanguage();

export default LanguageContext;
